<template>
  <div>
    <MenuButton fullWidth>
      <template #menu-button-trigger>
        <PickerTrigger :label="triggerLabel" :selected="Boolean(selectedShowingAgent)" :is-loading="isLoading" />
      </template>

      <MenuButtonItem
        v-for="showingAgent in showingAgents"
        :key="showingAgent.id"
        :title="getFullName(showingAgent)"
        :subtitle="showingAgent.phone ? formatPhoneNumber(showingAgent.phone) : noPhoneSubtitle"
        @click="chooseAgent(showingAgent)"
        icon="user-03"
      >
        <template #prefix>
          <ShowingAgentPickerAvatar :showingAgent="showingAgent" />
        </template>
      </MenuButtonItem>
      <!-- An empty menu would say nothing; the field is required, so say who can fill the list -->
      <View v-if="!isLoading && !showingAgents.length" direction="column" gap="xs" class="empty">
        <Text variant="label-medium">No showing agents yet</Text>
        <Text variant="body-small" color="secondary">
          {{ canAddAgents ? 'Add one below.' : "Ask this unit's manager to add one." }}
        </Text>
      </View>

      <MenuButtonItem
        v-if="canAddAgents"
        title="Add new agent"
        icon="plus"
        @click="addShowingAgentModalRef?.modalRef?.show()"
      >
        <template #prefix>
          <AvatarPlaceholder icon="plus" />
        </template>
      </MenuButtonItem>
    </MenuButton>

    <AddShowingAgentModel ref="addShowingAgentModalRef" :property-id="propertyId" @created="selectAgent" />
    <AddShowingAgentPhoneModal ref="addPhoneModalRef" @saved="selectAgent" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import { AvatarPlaceholder, MenuButton, MenuButtonItem, PickerTrigger, Text, View } from '@tallo/design-system';
import { getFullName, formatPhoneNumber } from '@tallo/utility';

import { ShowingAgentDto } from '../interfaces/showing-agent.dto';
import { usePropertyShowingAgentsQuery } from '../queries/property-showing-agents.query';
import { useShowingAgentsQuery } from '../queries/showing-agents.query';
import AddShowingAgentModel from './add-showing-agent-model.vue';
import AddShowingAgentPhoneModal from './add-showing-agent-phone-modal.vue';
import ShowingAgentPickerAvatar from './showing-agent-picker-avatar.vue';

const noPhoneSubtitle = 'No phone number yet — add one to pick them';

/** Given a unit, the picker offers that unit's own agents; without one, the company's whole roster */
const props = defineProps<{ propertyId?: string }>();
const model = defineModel();

const rosterQuery = useShowingAgentsQuery(() => !props.propertyId);
const unitQuery = usePropertyShowingAgentsQuery(() => props.propertyId);

const isLoading = computed(() => (props.propertyId ? unitQuery.isLoading.value : rosterQuery.isLoading.value));
const showingAgents = computed<ShowingAgentDto[]>(() =>
  props.propertyId ? (unitQuery.data.value?.agents ?? []) : (rosterQuery.data.value ?? []),
);
// Whoever may not change the unit's list may still pick from it, but not add to it
const canAddAgents = computed(() => !props.propertyId || Boolean(unitQuery.data.value?.canEdit));
const selectedShowingAgent = computed(() => showingAgents.value.find(({ id }) => id === model.value));
const triggerLabel = computed(() =>
  selectedShowingAgent.value ? getFullName(selectedShowingAgent.value) : 'Choose your showing agent',
);
const addShowingAgentModalRef = ref<InstanceType<typeof AddShowingAgentModel>>();
const addPhoneModalRef = ref<InstanceType<typeof AddShowingAgentPhoneModal>>();

// A cached list can predate an agent put on this unit since (by Emily, say), so the check below waits for a fresh one
const listIsCurrent = ref(false);

onMounted(async () => {
  if (props.propertyId) {
    await unitQuery.refresh();
  }

  listIsCurrent.value = true;
});

// A preselected agent the unit no longer lists (taken off it, or the default of another unit) is not sent unseen
watch(
  () => [unitQuery.data.value, model.value, listIsCurrent.value] as const,
  ([list, selectedId, isCurrent]) => {
    if (isCurrent && props.propertyId && list && selectedId && !list.agents.some(({ id }) => id === selectedId)) {
      model.value = undefined;
    }
  },
);

/** Renters are given the covering agent's number, so an agent without one is asked for it instead of picked */
function chooseAgent(showingAgent: ShowingAgentDto) {
  if (!showingAgent.phone) {
    addPhoneModalRef.value?.open(showingAgent);

    return;
  }

  selectAgent(showingAgent);
}

function selectAgent(showingAgent: ShowingAgentDto) {
  model.value = showingAgent.id;
}
</script>

<style scoped lang="scss">
.empty {
  max-width: 18rem;
  padding: var(--t-spacing-sm) var(--t-spacing-md);
}
</style>
